import { isSuperLollipopActive } from 'src/replicant/superLollipop/superLollipop.getters';
import {
  BasicBlockProps,
  ColorId,
  PowerBlockType,
  powerBlockTypes,
} from '../defs/block';
import { config } from '../defs/config';
import { BasicBlockEntity, BlockEntity } from '../entities/BlockEntity';
import { GameScene } from '../GameScene';
import {
  blockIsBinded,
  blockIsFrozen,
  blockIterateAll,
  blockIterateNeighbors,
} from './blockTools';
import { user } from 'src/StateObserver';
import { getSuperLollipopTargetColors } from 'src/game/superLollipop/superLollipop.utils';

// types
//-----------------------------------------------------------------------------
export type Match = {
  base: BlockEntity;
  overlay?: BlockEntity;
};

export type MatchGroup = {
  matches: Match[];
  // neighbors to above results
  neighbors: BlockEntity[];
};

// api
//-----------------------------------------------------------------------------
export function matchCheckTappable(scene: GameScene): boolean {
  const searched: BlockEntity[] = [];

  // iterate blocks
  return blockIterateAll(scene, (base, overlay) => {
    // if not overlayed
    if (!overlay || overlay.c.block.props.tappable) {
      // if a power block is tappable
      if (isPowerBlock(base)) return true;

      // if a basic group is tappable
      if (matchFindGroup(scene, base, searched)) return true;
    }

    return false;
  });
}

export function getColorMap(
  scene: GameScene,
): Record<Exclude<ColorId, 'all'>, BlockEntity[]> {
  const map: Record<Exclude<ColorId, 'all'>, BlockEntity[]> = {
    red: [],
    green: [],
    blue: [],
    yellow: [],
    purple: [],
  };
  blockIterateAll(scene, (base) => {
    if (base.c.block.props.type !== 'basic') return false;

    const color = (base.c.block.props as BasicBlockProps).color;

    if (color === 'all') return false;

    map[color].push(base);

    return false;
  });
  return map;
}

export function hasColorPair(scene: GameScene): boolean {
  return Object.values(getColorMap(scene)).some((list) => list.length >= 2);
}

export function matchFindGroups(scene: GameScene): MatchGroup[] {
  const groups: MatchGroup[] = [];
  const searched: BlockEntity[] = [];

  // iterate blocks
  blockIterateAll(scene, (base, overlay) => {
    // if not overlayed or if overlay is non-blocker, add basic group if matchable
    if (!overlay || overlay?.c.block.props.tappable) {
      const group = matchFindGroup(scene, base, searched);
      if (group) groups.push(group);
    }
  });

  return groups;
}

export function matchFindGroup(
  scene: GameScene,
  entity: BlockEntity,
  searched: BlockEntity[] = [],
): MatchGroup | undefined {
  const block = entity.c.block;

  // require basic initial block
  if (block.props.type === 'basic') {
    const group: MatchGroup = {
      matches: [],
      neighbors: [],
    };

    // iterate neighbor blocks starting at given entity
    blockIterateNeighbors(
      scene.sessionEntity.c.map,
      entity,
      (base, overlay) => {
        // end if does not match our id or is frozen or binded. track as neighbor instead.
        if (
          block.blockId !== base.c.block.blockId ||
          blockIsFrozen(scene, base) ||
          blockIsBinded(scene, base)
        ) {
          group.neighbors.push(base);
          return false;
        }

        // fail if already searched
        if (searched.includes(base)) {
          group.matches = [];
          return false;
        }

        // add to matches
        group.matches.push({
          base,
          overlay,
        });

        // add to searched
        searched.push(base);

        return true;
      },
    );

    // success if a match group length
    if (group.matches.length >= config.blocks.basic.match) return group;
  }

  return undefined;
}

export function isPowerBlock(entity: BlockEntity): boolean {
  return powerBlockTypes.includes(entity.c.block.props.type as PowerBlockType);
}

/**
 * Check if the board should be shuffled.
 * @param scene
 * @returns
 */
let alreadyShuffled = false;
export function shouldShuffle(scene: GameScene) {
  // Check if the board has already been shuffled.
  if (alreadyShuffled) return false;

  // Check if there are tapable blocks and no color pairs.
  // This is a special case where a shuffle wouldn't work. And there're still actions available.
  const hasTappableWithoutPairs =
    matchCheckTappable(scene) && !hasColorPair(scene);

  // Check if there are any matches.
  const hasMatches = matchFindGroups(scene).length > 0;

  // Check if the player is out of moves.
  const outOfMoves = scene.moves <= 0;

  return !hasTappableWithoutPairs && !hasMatches && !outOfMoves;
}

export function setAlreadyShuffled(value: boolean) {
  alreadyShuffled = value;
}

/**
 * Retrieve all basic blocks of given color that can be targeted by the lollipop (cube).
 * @param scene
 * @param color
 * @returns
 */
export function getCubeTargets(
  scene: GameScene,
  color: ColorId,
): BlockEntity[] {
  const targets: BasicBlockEntity[] = [];

  // Iterate blocks
  blockIterateAll(scene, (base) => {
    const block = base?.c.block;

    // Only basic blocks can be targeted by the regular lollipop (cube)
    if (block?.props.type !== 'basic') {
      return false;
    }

    // Cannot be target if frozen or binded
    if (blockIsFrozen(scene, base) || blockIsBinded(scene, base)) {
      return false;
    }

    // All remaining basic blocks are possble targets - it will be filtered by colors afterwards
    targets.push(base as BasicBlockEntity);

    // Return false to continue iteration
    return false;
  });

  // Primary color should be always set
  let primaryColor: ColorId = color;
  let secondaryColor: ColorId = color;

  // Include a secondary color to be target, if super lollipop is active
  if (isSuperLollipopActive(user())) {
    const targetColors = targets.map(
      (target: BasicBlockEntity) =>
        (target.c.block.props as BasicBlockProps).color,
    );
    // Here the primary color might change if there was no target with given color
    const selectedColors = getSuperLollipopTargetColors(
      primaryColor,
      targetColors,
    );
    primaryColor = selectedColors.primary;
    secondaryColor = selectedColors.secondary;
  }

  // Retrieve only the targets that match the color or secondary color
  const result = targets.filter((target: BasicBlockEntity) => {
    const props = target.c.block.props as BasicBlockProps;
    return primaryColor === props.color || secondaryColor === props.color;
  });

  // Sort targets by color: primary color first, then secondary color
  result.sort((a, b) => {
    const aColor = (a.c.block.props as BasicBlockProps).color;
    const bColor = (b.c.block.props as BasicBlockProps).color;
    return aColor === primaryColor ? -1 : bColor === primaryColor ? 1 : 0;
  });

  return result;
}

/**
 * Split targets into two groups: primary targets and secondary targets.
 * @param scene
 * @param color
 * @returns
 */
export function getCubeTargetGroups(targets: BlockEntity[]): BlockEntity[][] {
  if (!targets.length) {
    return [];
  }
  const color = (targets[0].c.block.props as BasicBlockProps).color;
  const result: BlockEntity[][] = [];

  // Find out the primary targets for this effect
  const primaryTargets = targets.filter((target) => {
    const targetProps = target.c.block.props as BasicBlockProps;
    return targetProps.color === color;
  });
  if (primaryTargets.length) {
    result.push(primaryTargets);
  }

  // Secondary targest are the targets not in the
  const secondaryTargets = targets.filter((target) => {
    const targetProps = target.c.block.props as BasicBlockProps;
    return targetProps.color !== color;
  });
  if (secondaryTargets.length) {
    result.push(secondaryTargets);
  }

  return result;
}
